/**
 * Top-level React error boundary.
 *
 * Without this, an uncaught render error (a null dereference, a bad prop)
 * unmounts the entire tree. React shows nothing in its place — not even a
 * technical message, just a blank page — which is the one gap the error
 * message audit couldn't close by rewording toasts, because a crashed React
 * tree can't render a toast either.
 *
 * Error boundaries only catch errors during rendering, in lifecycle methods,
 * and in constructors of the tree below them — not in event handlers, async
 * code, or the boundary's own render. Those paths are covered by
 * toUserMessage() at their own catch sites; this is the backstop for
 * whatever neither of those pre-empted.
 */
import React from 'react';
import {
  Box,
  Button,
  Center,
  Heading,
  Text,
  VStack,
  Code,
} from '@chakra-ui/react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // No client-side error tracker (Sentry et al.) is wired into the
    // frontend today — this is the only record of a crash until one is.
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <Center h="100vh" bg="bg.primary" px={4}>
        <VStack spacing={4} maxW="md" textAlign="center">
          <Heading size="lg" color="text.primary">
            Something went wrong
          </Heading>
          <Text color="text.secondary">
            We hit an unexpected error and couldn't continue. Reloading the
            page usually fixes it — if it keeps happening, please contact
            support.
          </Text>
          <Button colorScheme="blue" onClick={this.handleReload}>
            Reload page
          </Button>
          {import.meta.env.DEV && (
            <Box
              as="pre"
              mt={4}
              p={3}
              bg="bg.surface"
              borderRadius="md"
              fontSize="xs"
              textAlign="left"
              overflowX="auto"
              maxW="100%"
            >
              <Code whiteSpace="pre-wrap" bg="transparent">
                {this.state.error.stack || this.state.error.message}
              </Code>
            </Box>
          )}
        </VStack>
      </Center>
    );
  }
}
